import React, { useEffect, useState, useRef, useCallback } from "react";
import { io } from "socket.io-client";
import EmojiPicker from "emoji-picker-react";
import { useNavigate, useParams } from "react-router-dom";
import "./Chat.css";
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import isToday from 'dayjs/plugin/isToday';
import isYesterday from 'dayjs/plugin/isYesterday';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isToday);
dayjs.extend(isYesterday);

const SOCKET_URL = "https://razchat-backend-1.onrender.com";

export default function Chat() {
  const { userId } = useParams();
  const [messages, setMessages] = useState([]);
  const [content, setContent] = useState("");
  const [chatUser, setChatUser] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [showEmoji, setShowEmoji] = useState(false);
  const [alertMode, setAlertMode] = useState(false);
  const [socket, setSocket] = useState(null);
  const [myUserId, setMyUserId] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState("connecting");
  const [reconnectAttempts, setReconnectAttempts] = useState(0);
  const token = localStorage.getItem("token");
  const messagesEndRef = useRef(null);
  const navigate = useNavigate();
  const socketRef = useRef(null);
  const maxReconnectAttempts = 5;

  // Helper: mark all unseen messages in this chat as seen
  const markMessagesAsSeen = useCallback((msgs) => {
    const unseen = msgs.filter(
      (m) => m.receiver === myUserId && String(m.sender) === String(userId) && !m.seen
    );
    if (unseen.length > 0) {
      fetch(`https://razchat-backend-1.onrender.com/api/chat/seen/${userId}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      }).catch(err => console.error('Error marking messages as seen:', err));
      
      setMessages((prev) =>
        prev.map((m) =>
          m.receiver === myUserId && String(m.sender) === String(userId) ? { ...m, seen: true } : m
        )
      );
    }
  }, [myUserId, userId, token]);

  // Robust socket connection function
  const createSocketConnection = useCallback((tokenData) => {
    if (socketRef.current) {
      socketRef.current.disconnect();
    }

    console.log('Attempting to connect to WebSocket...');
    setConnectionStatus("connecting");

    const socketOptions = {
      transports: ["websocket", "polling"], // Fallback to polling if websocket fails
      timeout: 10000, // 10 second timeout
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      maxReconnectionAttempts: maxReconnectAttempts,
      forceNew: true,
      upgrade: true,
      rememberUpgrade: true
    };

    const s = io(SOCKET_URL, socketOptions);
    socketRef.current = s;

    // Connection event handlers
    s.on("connect", () => {
      console.log('WebSocket connected successfully');
      setConnectionStatus("connected");
      setReconnectAttempts(0);
      setError("");
      
      if (tokenData?.userId || myUserId) {
        s.emit("joinRoom", tokenData?.userId || myUserId);
      }
    });

    s.on("connect_error", (err) => {
      console.error('WebSocket connection error:', err);
      setConnectionStatus("error");
      setError(`Connection failed: ${err.message}`);
    });

    s.on("disconnect", (reason) => {
      console.log('WebSocket disconnected:', reason);
      setConnectionStatus("disconnected");
      
      if (reason === "io server disconnect") {
        // Server initiated disconnect, try to reconnect
        setTimeout(() => {
          if (reconnectAttempts < maxReconnectAttempts) {
            setReconnectAttempts(prev => prev + 1);
            createSocketConnection(tokenData);
          }
        }, 2000);
      }
    });

    s.on("reconnect", (attemptNumber) => {
      console.log('WebSocket reconnected after', attemptNumber, 'attempts');
      setConnectionStatus("connected");
      setReconnectAttempts(0);
    });

    s.on("reconnect_error", (err) => {
      console.error('WebSocket reconnection error:', err);
      setConnectionStatus("error");
    });

    s.on("reconnect_failed", () => {
      console.error('WebSocket reconnection failed');
      setConnectionStatus("failed");
      setError("Unable to connect to server. Please refresh the page.");
    });

    // Message event handlers
    s.on("newMessage", (msg) => {
      console.log('Received new message:', msg);
      
      const isMessageForCurrentChat = 
        (String(msg.sender) === String(userId) && msg.receiver === myUserId) ||
        (msg.sender === myUserId && String(msg.receiver) === String(userId));

      if (isMessageForCurrentChat) {
        setMessages(prev => {
          const updated = [...prev, msg];
          setTimeout(() => markMessagesAsSeen(updated), 0);
          return updated;
        });

        if (
          msg.receiver === myUserId &&
          String(msg.sender) === String(userId) &&
          window.Notification &&
          Notification.permission === "granted"
        ) {
          new Notification("New message", {
            body: msg.content,
            icon: "./logo.png",
          });
        }
      }
    });

    s.on("notification", (notif) => {
      if (window.Notification && Notification.permission === "granted") {
        if (notif.type === "follow-request") {
          new Notification("Follow Request", {
            body: `${notif.user.username} sent you a follow request!`,
            icon: "./logo.png",
          });
        }
        if (notif.type === "follow-accepted") {
          new Notification("Follow Accepted", {
            body: `${notif.user.username} accepted your follow request!`,
            icon: "./logo.png",
          });
        }
      }
    });

    s.on("messagesSeen", ({ by }) => {
      if (String(by) === String(userId)) {
        setMessages(prev =>
          prev.map(m =>
            m.sender === myUserId && String(m.receiver) === String(userId) ? { ...m, seen: true } : m
          )
        );
      }
    });

    setSocket(s);
    return s;
  }, [userId, myUserId, markMessagesAsSeen, reconnectAttempts]);

  // Focus & visibilitychange handler for desktop
  useEffect(() => {
    function handleFocusVisibility() {
      if (!loading && messages.length > 0 && myUserId) {
        markMessagesAsSeen(messages);
      }
    }
    window.addEventListener("focus", handleFocusVisibility);
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") {
        handleFocusVisibility();
      }
    });
    return () => {
      window.removeEventListener("focus", handleFocusVisibility);
      document.removeEventListener("visibilitychange", handleFocusVisibility);
    };
  }, [loading, messages, myUserId, markMessagesAsSeen]);

  useEffect(() => {
    if (!token) {
      navigate("/login");
      return;
    }

    // Clear messages when switching to a different chat
    setMessages([]);
    setLoading(true);
    setError("");
    
    let tokenData;
    try {
      tokenData = JSON.parse(atob(token.split('.')[1]));
      setMyUserId(tokenData.userId);
    } catch (err) {
      console.error('Invalid token:', err);
      setMyUserId(null);
    }

    // Fetch chat user info
    fetch(`https://razchat-backend-1.onrender.com/api/users/${userId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(res => {
        if (!res.ok) throw new Error('Failed to fetch user');
        return res.json();
      })
      .then(setChatUser)
      .catch(err => {
        console.error('Error fetching chat user:', err);
        setError("Failed to load user information");
      });

    // Fetch messages
    fetch(`https://razchat-backend-1.onrender.com/api/chat/with/${userId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(res => {
        if (!res.ok) throw new Error('Failed to fetch messages');
        return res.json();
      })
      .then(data => {
        setMessages(data);
        setLoading(false);
        setTimeout(() => markMessagesAsSeen(data), 100);
      })
      .catch(err => {
        console.error('Error fetching messages:', err);
        setError("Failed to load messages");
        setLoading(false);
      });

    // Create socket connection with delay to ensure user data is set
    const connectionTimeout = setTimeout(() => {
      createSocketConnection(tokenData);
    }, 500);

    return () => {
      clearTimeout(connectionTimeout);
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [userId, token, createSocketConnection, markMessagesAsSeen, navigate]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (!loading && messages.length > 0 && myUserId) {
      markMessagesAsSeen(messages);
    }
  }, [messages, myUserId, loading, markMessagesAsSeen]);

  const sendMessage = async e => {
    e.preventDefault();
    setError("");
    if (!content.trim()) return;

    // Check if socket is connected
    if (!socket || !socket.connected) {
      setError("Connection lost. Please refresh the page.");
      return;
    }

    const url = alertMode
      ? "https://razchat-backend-1.onrender.com/api/chat/alert"
      : "https://razchat-backend-1.onrender.com/api/chat/send";

    const now = dayjs().tz('Asia/Kolkata');
    const istDate = now.format('YYYY-MM-DD');
    const istTime = now.format('hh:mm:ss A');

    try {
      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ to: userId, content, date: istDate, time: istTime }),
      });

      if (res.ok) {
        setContent("");
        setAlertMode(false);
        await res.json();
        
        // Refresh messages
        const messagesRes = await fetch(`https://razchat-backend-1.onrender.com/api/chat/with/${userId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        
        if (messagesRes.ok) {
          const messagesData = await messagesRes.json();
          setMessages(messagesData);
        }
      } else if (res.status === 403) {
        const data = await res.json();
        setError(data.error || "You must follow this user to chat.");
      } else {
        setError("Failed to send message. Please try again.");
      }
    } catch (err) {
      console.error('Error sending message:', err);
      setError("Network error. Please check your connection.");
    }
  };

  // Retry connection function
  const retryConnection = () => {
    setReconnectAttempts(0);
    let tokenData;
    try {
      tokenData = JSON.parse(atob(token.split('.')[1]));
    } catch {}
    createSocketConnection(tokenData);
  };

  function groupMessagesByDate(msgs) {
    if (!msgs || msgs.length === 0) return [];
    const groups = [];
    let curDate = null;
    let curGroup = [];

    msgs.forEach((m, idx) => {
      let msgDate = m.date;
      if (!msgDate && m.createdAt) {
        msgDate = dayjs(m.createdAt).tz('Asia/Kolkata').format('YYYY-MM-DD');
      }
      if (!msgDate) msgDate = dayjs().tz('Asia/Kolkata').format('YYYY-MM-DD');

      if (curDate !== msgDate) {
        if (curGroup.length > 0) groups.push({ date: curDate, messages: curGroup });
        curDate = msgDate;
        curGroup = [];
      }
      curGroup.push(m);
    });
    if (curGroup.length > 0) groups.push({ date: curDate, messages: curGroup });
    return groups;
  }

  function formatDateLabel(dateStr) {
    if (!dateStr) return "";
    const d = dayjs(dateStr).tz('Asia/Kolkata');
    if (d.isToday()) return "Today";
    if (d.isYesterday()) return "Yesterday";
    return d.format('DD MMM YYYY');
  }

  function getLastSentMessage() {
    const sent = messages.filter(
      (m) => m.sender === myUserId || m.isMine
    );
    if (sent.length === 0) return null;
    return sent[sent.length - 1];
  }

  const groupedMessages = groupMessagesByDate(messages);
  const lastSent = getLastSentMessage();

  return (
    <div className="dm-chat-bg">
      <div className="dm-chat-topbar">
        <button
          className="dm-chat-back-btn"
          onClick={() => navigate("/dashboard")}
        >
          <span className="material-symbols-outlined">arrow_back</span>
        </button>
        <span className="dm-chat-title">
          {chatUser ? chatUser.username?.split(" ")[0] || chatUser.email : "Messages"}
        </span>
        
        {/* Connection Status Indicator */}
        <div className="connection-status" style={{ marginLeft: 'auto', marginRight: '10px' }}>
          {connectionStatus === "connecting" && (
            <span style={{ color: '#ffa500', fontSize: '12px' }}>Connecting...</span>
          )}
          {connectionStatus === "connected" && (
            <span style={{ color: '#4caf50', fontSize: '12px' }}>●</span>
          )}
          {connectionStatus === "disconnected" && (
            <span style={{ color: '#ff9800', fontSize: '12px' }}>Reconnecting...</span>
          )}
          {(connectionStatus === "error" || connectionStatus === "failed") && (
            <button 
              onClick={retryConnection}
              style={{ 
                background: '#ff5722', 
                color: 'white', 
                border: 'none', 
                borderRadius: '4px', 
                padding: '2px 6px', 
                fontSize: '10px',
                cursor: 'pointer'
              }}
            >
              Retry
            </button>
          )}
        </div>
      </div>
      
      <>
        <div className="dm-chat-thread">
          {loading ? (
            <div className="dm-chat-loader" />
          ) : (
            <>
              {groupedMessages.map((group, gidx) => (
                <React.Fragment key={group.date || gidx}>
                  <div className="dm-chat-date-label">
                    {formatDateLabel(group.date)}
                  </div>
                  {group.messages.map((m, idx) => {
                    const isMine = m.sender === myUserId || m.isMine;
                    const isLastSent = lastSent && lastSent._id === m._id;
                    return (
                      <div
                        key={m._id || idx}
                        className={`dm-chat-bubble${
                          isMine ? " dm-chat-bubble-right" : " dm-chat-bubble-left"
                        } dm-chat-bubble-animate`}
                      >
                        {!isMine && (
                          <div className="dm-chat-bubble-name">
                            {m.senderName ? m.senderName.split(" ")[0] : ""}
                          </div>
                        )}
                        <div className="dm-chat-bubble-content">{m.content}</div>
                        <div className="dm-chat-bubble-time-row">
                          <span className="dm-chat-bubble-time">{m.time ? m.time : ""}</span>
                          {isMine && isLastSent && (
                            <span className="dm-chat-seen-indicator">
                              {m.seen ? (
                                <span title="Seen" style={{ color: "#0088ff", fontSize: "0.9em", marginLeft: 6 }}>
                                  ✔✔
                                </span>
                              ) : (
                                <span title="Delivered" style={{ color: "#aaa", fontSize: "0.9em", marginLeft: 6 }}>
                                  ✔
                                </span>
                              )}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </React.Fragment>
              ))}
              <div ref={messagesEndRef} />
            </>
          )}
        </div>
        
        <form className="dm-chat-inputbar" onSubmit={sendMessage}>
          <div style={{ position: "relative" }}>
            <button
              type="button"
              className="dm-chat-emoji-btn"
              onClick={() => setShowEmoji(v => !v)}
              tabIndex={-1}
            >
              <span className="material-symbols-outlined">mood</span>
            </button>
            {showEmoji && (
              <div style={{ position: "absolute", bottom: 48, left: 0, zIndex: 100 }}>
                <EmojiPicker
                  onEmojiClick={(emojiObject) => {
                    setContent(content + (emojiObject.emoji || ""));
                    setShowEmoji(false);
                  }}
                  theme="light"
                  width={320}
                  height={350}
                />
              </div>
            )}
          </div>
          <input
            className="dm-chat-input"
            value={content}
            onChange={e => setContent(e.target.value)}
            placeholder={
              connectionStatus === "connected" 
                ? "Type a message..." 
                : "Connecting..."
            }
            disabled={connectionStatus !== "connected"}
          />
          <button
            type="button"
            className={`dm-chat-emoji-btn${alertMode ? " dm-chat-alert-active" : ""}`}
            title="Send as alert"
            style={{ color: alertMode ? "#fd1d1d" : undefined, fontWeight: alertMode ? "bold" : undefined }}
            onClick={() => setAlertMode(v => !v)}
            disabled={connectionStatus !== "connected"}
          >
            <span className="material-symbols-outlined">notification_important</span>
          </button>
          <button 
            type="submit" 
            className="dm-chat-send-btn"
            disabled={connectionStatus !== "connected"}
          >
            <span className="material-symbols-outlined">send</span>
          </button>
        </form>
      </>
      
      {error && (
        <div className="dm-chat-error" style={{ 
          background: '#ff5722', 
          color: 'white', 
          padding: '10px', 
          margin: '10px',
          borderRadius: '4px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span>{error}</span>
          {(connectionStatus === "error" || connectionStatus === "failed") && (
            <button 
              onClick={retryConnection}
              style={{ 
                background: 'white', 
                color: '#ff5722', 
                border: 'none', 
                borderRadius: '4px', 
                padding: '4px 8px',
                cursor: 'pointer'
              }}
            >
              Retry
            </button>
          )}
        </div>
      )}
      
      <link href="https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined" rel="stylesheet" />
    </div>
  );
}